import React, { useState, useRef } from 'react';
import { uploadCSVBatch } from '../services/api';
import { CustomField } from '../types';
import CustomFieldsEditor from './CustomFieldsEditor';
import './CSVUpload.css';

interface CSVUploadProps {
  projectId: string;
  onUploadStart: (jobId: string, customFields: CustomField[]) => void;
}

const CSVUpload: React.FC<CSVUploadProps> = ({ projectId, onUploadStart }) => {
  const [file, setFile] = useState<File | null>(null);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [showFieldsEditor, setShowFieldsEditor] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (!droppedFile.name.endsWith('.csv')) {
        setError('Please drop a CSV file');
        return;
      }
      setFile(droppedFile);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setIsUploading(true);
    setError(null);

    try {
      const result = await uploadCSVBatch(file, projectId, customFields);
      onUploadStart(result.job_id, customFields);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="csv-upload">
      <h2>📤 Upload Questionnaire CSV</h2>
      <p className="upload-description">
        Upload a CSV file with a "Query" or "Question" column containing your compliance questions.
        The system will process each question and generate responses with evidence.
      </p>

      <div
        className={`drop-zone ${file ? 'has-file' : ''}`}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          type="file"
          ref={fileInputRef}
          accept=".csv"
          onChange={handleFileChange}
          hidden
        />
        {file ? (
          <div className="file-info">
            <span className="file-icon">📄</span>
            <span className="file-name">{file.name}</span>
            <span className="file-size">({(file.size / 1024).toFixed(1)} KB)</span>
          </div>
        ) : (
          <div className="drop-prompt">
            <span className="upload-icon">📁</span>
            <span>Drag & drop your CSV file here, or click to browse</span>
          </div>
        )}
      </div>

      {error && <div className="upload-error">{error}</div>}

      <div className="upload-actions">
        <button
          className="fields-button"
          onClick={() => setShowFieldsEditor(!showFieldsEditor)}
        >
          📋 Custom Fields ({customFields.length})
        </button>
        <button
          className="upload-button"
          onClick={handleUpload}
          disabled={!file || isUploading}
        >
          {isUploading ? '⏳ Uploading...' : '🚀 Process CSV'}
        </button>
      </div>

      {showFieldsEditor && (
        <CustomFieldsEditor
          fields={customFields}
          onFieldsChange={setCustomFields}
          onClose={() => setShowFieldsEditor(false)}
        />
      )}

      <div className="format-hint">
        <h4>Expected CSV Format:</h4>
        <pre>
{`Query,Category,Priority
What is your password policy?,Security,High
Do you encrypt data at rest?,Data Protection,Medium`}
        </pre>
      </div>
    </div>
  );
};

export default CSVUpload;

